import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Student, StudentTags } from '@chancenwerk/master-data-management/users/entities/Student.js';
import type { Filter } from 'mongodb';

/**
 * Finds similar students to the given student. Similarity is based on the first and last name.
 * The student must have a guest account and the tag FROM_OS_ACQUISITION.
 *
 * @query findSimilarOSAcquisitionStudent
 * @param similar - The similar Student to search for
 */
export const findSimilarOSAcquisitionStudent = registerQuery({
  type: () => [Student],
  operationName: 'findSimilarOSAcquisitionStudent',
  resolve: (similar: Student) => {
    return async (gqlFields: BaseQLSelectionSet<Student>) => {
      const col = await getRawCollection<Student>(Student._collectionName);

      // vorname and nachname are required
      if (!similar.vorname || !similar.nachname || similar.vorname === '' || similar.nachname === '') {
        throw new Error('vorname and nachname are required');
      }

      const query: Filter<Student> = {
        $and: [
          { vorname: { $regex: similar.vorname, $options: 'i' } },
          { nachname: { $regex: similar.nachname, $options: 'i' } },
          { guestAccount: true },
          { tags: { $all: [StudentTags.FROM_OS_ACQUISITION] } },
        ],
      };

      if (similar.email) {
        query.$and?.push({ email: similar.email });
      }

      if (similar.handynummer) {
        query.$and?.push({ handynummer: similar.handynummer });
      }

      const res = await col
        .find(query, {
          projection: {
            _id: 1,
            _class: 1,
            vorname: 1,
            nachname: 1,
            email: 1,
            handynummer: 1,
          },
        })
        .limit(15)
        .toArray();

      return res as Student[];
    };
  },
  options: {},
  params: [{ type: () => Student, name: 'similar' }],
});
